import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ConnectButton } from "@/components/ui/ConnectButton";
import {
  LogIn,
  Calculator,
  LogOut,
  CircleUser,
  BookOpen,
  SplitSquareHorizontal,
  Shield,
  Settings,
  Globe,
  Library,
  Menu,
  X,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  History,
  Cpu,
} from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useMyMap } from "@/features/maps/core/useMyMap";
import {
  useRecentCalculatedMaps,
  getMapDeduplicationKey,
  RecentCalculatedMap,
} from "@/features/maps/core/useRecentCalculatedMaps";
import { ClaimSelfMapModal } from "@/components/dialogs";
import { Modal } from "@/components/ui/Modal";
import { SocialLogin } from "@/features/auth/components/SocialLogin";
import { EmailLoginModal } from "@/features/auth/components/EmailLoginModal";
import { CheckoutModal } from "@/features/payment/components/CheckoutModal";
import { toast } from "@/stores/notificationStore";
import { analyticsService } from "@/services/AnalyticsService";

export const Header: React.FC = () => {
  const { user, signOut, isAdmin, isPremium, isLoginModalOpen, setIsLoginModalOpen, credits } = useAuth();
  const { myMap, loading: isMyMapLoading } = useMyMap();
  const { recentMaps, removeMap } = useRecentCalculatedMaps();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRecentMapsOpen, setIsRecentMapsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isEmailLoginModalOpen, setIsEmailLoginModalOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setIsRecentMapsOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleRecentMapClick = (mapItem: RecentCalculatedMap) => {
    closeDropdown();
    setIsMobileMenuOpen(false);

    const params = new URLSearchParams();
    if (mapItem.id) params.set('id', mapItem.id);
    params.set('firstName', mapItem.firstName);
    if (mapItem.familyName) params.set('familyName', mapItem.familyName);
    if (mapItem.motherName) params.set('motherName', mapItem.motherName);
    params.set('birthDate', mapItem.birthDate);
    if (mapItem.birth_time) params.set('birthTime', mapItem.birth_time);
    if (mapItem.birth_lat !== undefined && mapItem.birth_lat !== null) params.set('lat', String(mapItem.birth_lat));
    if (mapItem.birth_lon !== undefined && mapItem.birth_lon !== null) params.set('lng', String(mapItem.birth_lon));
    if (mapItem.city_name) params.set('cityName', mapItem.city_name);
    if (mapItem.gender) params.set('gender', mapItem.gender);
    if (mapItem.timezone) params.set('timezone', mapItem.timezone);
    if (mapItem.sunLongitude !== undefined && mapItem.sunLongitude !== null) params.set('sunLongitude', String(mapItem.sunLongitude));
    if (mapItem.soul_root) params.set('soulRoot', mapItem.soul_root);
    params.set('results', 'true');
    navigate(`/calculator?${params.toString()}`);
  };

  const handleMyMapClick = () => {
    closeDropdown();
    setIsMobileMenuOpen(false);
    if (myMap) {
      const astroSunLon = (myMap.astrology_cache as { planets?: { sun?: { longitude?: number } } } | undefined)?.planets?.sun?.longitude;
      handleRecentMapClick({
        id: myMap.id,
        firstName: myMap.firstName || '',
        familyName: myMap.familyName || undefined,
        motherName: myMap.motherName || undefined,
        birthDate: myMap.birthDate || '',
        birth_time: myMap.birth_time || undefined,
        birth_lat: myMap.birth_lat === null ? undefined : myMap.birth_lat,
        birth_lon: myMap.birth_lon === null ? undefined : myMap.birth_lon,
        city_name: myMap.birth_location || undefined,
        gender: myMap.gender || undefined,
        timezone: myMap.timezone || undefined,
        sunLongitude: astroSunLon,
        astrology_cache: myMap.astrology_cache,
        soul_root: myMap.soul_root || undefined,
        calculatedAt: Date.now()
      });
    } else {
      // Open Onboarding
      setIsOnboardingOpen(true);
    }
  };

  React.useEffect(() => {
    const handleOpenCheckout = () => {
      setIsCheckoutOpen(true);
    };
    window.addEventListener('open-checkout', handleOpenCheckout);
    return () => {
      window.removeEventListener('open-checkout', handleOpenCheckout);
    };
  }, []);

  const navItems = [
    { label: "מחשבון", path: "/calculator", icon: Calculator, title: "מחשבון" },
    { label: "מפות שמורות", path: "/saved-maps", icon: Library, title: "מפות שמורות" },
    { label: "השוואת מפות", path: "/compare-maps", icon: SplitSquareHorizontal, title: "השוואת מפות" },
    { label: "מצב השמיים", path: "/sky-state", icon: Globe, title: "מצב השמיים" },
    { label: "מרכז המידע", path: "/knowledge-center", icon: BookOpen, title: "מרכז המידע" },
  ];

  return (
    <>
      <header className="glass-card border-b border-white/10 sticky top-0 z-50 transition-all duration-300">
        <div className="container mx-auto px-4 py-1.5 min-[900px]:py-1.5 transition-all duration-300">
          <div className="flex items-center justify-between w-full">
            {/* Right Side (RTL Start) - Logo Group + Desktop Navigation + Mobile Menu */}
            <div className="flex items-center gap-2 md:gap-4 overflow-hidden">
              {/* Mobile Menu Toggle (< 1024px) - MOVED TO RIGHT SIDE */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="sm:hidden p-2 text-mystic-200 hover:bg-white/5 rounded-lg transition-colors shrink-0"
                aria-label="תפריט"
              >
                {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
              </button>

              <div
                className="flex items-center gap-2 md:gap-4 lg:space-x-reverse cursor-pointer group shrink-0"
                dir="ltr"
                onClick={() => navigate("/")}
              >
                {/* The Golden Flask - Responsive size - Hidden on Home */}
                {!isHome && (
                  <img
                    src="/assets/flask-only-no-bg.png"
                    alt="Spirit Research Lab Flask"
                    className="w-8 h-8 min-[900px]:w-11 min-[900px]:h-11 object-contain drop-shadow-[0_0_20px_rgba(255,215,0,0.4)] transition-all duration-500 group-hover:scale-105 shrink-0"
                  />
                )}

                {/* Logo Text Variants - High Visibility on Large screens */}
                <div className="block pt-1">
                  {/* Tablet Monogram */}
                  <img
                    src="/assets/srl-monogram-no-bg.png"
                    alt="SRL Monogram"
                    className="block min-[900px]:hidden w-10 h-10 md:w-12 md:h-12 object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100 shrink-0"
                  />

                  {/* Desktop Full Stacked Text - Now starting at 900px */}
                  <div className="hidden min-[900px]:block pt-1">
                    <img src="/assets/spirit-text-no-bg.png" alt="Spirit" className="block h-[14px] min-[900px]:h-[15px] lg:h-[16px] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100 mb-0.5" />
                    <img src="/assets/research-text-no-bg.png" alt="Research" className="block h-[14px] min-[900px]:h-[15px] lg:h-[16px] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100 mb-0.5" />
                    <img src="/assets/lab-text-no-bg.png" alt="Lab" className="block h-[12px] min-[900px]:h-[13px] lg:h-[14px] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                  </div>
                </div>
              </div>

              {/* Desktop Navigation Links (>= 640px) */}
              <nav className="hidden sm:flex items-center gap-4">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className="flex items-center gap-2 whitespace-nowrap shrink-0 text-mystic-200 hover:text-gold-400 transition-colors duration-300 bg-transparent border-none cursor-pointer p-0"
                    title={item.title}
                  >
                    <item.icon size={20} className="hover:scale-110 transition-transform duration-300" />
                    <span className="hidden lg:inline">{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Left Side (RTL End) - Auth Only */}
            <div className="flex items-center shrink-0 justify-end gap-2 md:gap-4">
              <button
                onClick={() => {
                  if (user) {
                    setIsCheckoutOpen(true);
                  } else {
                    setIsLoginModalOpen(true);
                  }
                  analyticsService.track('premium_button_click', { source: 'header' });
                }}
                className={`flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-lg transition-all duration-300 hover:scale-105 active:scale-95 text-sm font-bold shrink-0 backdrop-blur-md ${(!user || isPremium)
                    ? "text-gold-200 hover:text-white bg-gradient-to-r from-gold-500/10 via-amber-500/15 to-yellow-600/10 hover:from-gold-500/20 hover:via-amber-500/30 hover:to-yellow-500/20 border border-gold-500/25 hover:border-gold-400 shadow-[0_0_15px_rgba(234,179,8,0.05)] hover:shadow-[0_0_30px_rgba(234,179,8,0.3)]"
                    : "text-purple-200 hover:text-white bg-gradient-to-r from-purple-500/10 via-fuchsia-500/15 to-violet-600/10 hover:from-purple-500/20 hover:via-fuchsia-500/30 hover:to-violet-500/20 border border-purple-500/25 hover:border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.05)] hover:shadow-[0_0_30px_rgba(168,85,247,0.3)]"
                  }`}
                title="פרימיום וקרדיטים"
              >
                <Sparkles size={16} className={`animate-pulse ${(!user || isPremium) ? "text-gold-400" : "text-purple-400"}`} />
                <span className="font-black">
                  {user ? `${credits || 0} קרדיטים` : "פרימיום וקרדיטים"}
                </span>
              </button>

              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(!isDropdownOpen);
                      setIsRecentMapsOpen(false);
                    }}
                    className="flex items-center gap-2 md:gap-3 hover:bg-white/5 p-1.5 md:p-2 rounded-lg transition-colors border border-transparent hover:border-white/10 relative z-50 shrink-0"
                  >
                    {!isMyMapLoading && myMap === null && (
                      <span className="absolute top-1 right-1 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    )}
                    <div className="flex items-center gap-2 text-gold-100 text-sm whitespace-nowrap shrink-0">
                      <span className="hidden min-[460px]:inline font-medium">
                        {user.user_metadata?.full_name ||
                          user.user_metadata?.name ||
                          user.email?.split("@")[0]}
                      </span>
                      {user.user_metadata?.avatar_url ||
                        user.user_metadata?.picture ? (
                        <img
                          src={
                            user.user_metadata.avatar_url ||
                            user.user_metadata.picture
                          }
                          alt="User Avatar"
                          className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gold-400 object-cover shrink-0"
                        />
                      ) : (
                        <CircleUser size={22} className="text-gold-200 shrink-0" />
                      )}
                    </div>
                  </button>

                  {isDropdownOpen && (
                    <>
                      <div className="absolute top-full left-0 mt-2 w-52 bg-[#0d091a] backdrop-blur-2xl border border-gold-500/40 rounded-xl shadow-2xl shadow-black/80 z-50 animate-in fade-in zoom-in-95 duration-200" dir="rtl">
                        <div className="py-1">
                          <button
                            onClick={handleMyMapClick}
                            className="w-full px-4 py-2.5 text-right text-mystic-200 hover:bg-white/10 hover:text-gold-400 transition-colors flex items-center justify-end gap-3 text-xs font-bold"
                          >
                            <span className="whitespace-nowrap flex items-center gap-1.5">
                              המפה שלי
                              {!isMyMapLoading && myMap === null && (
                                <span className="flex h-1.5 w-1.5 relative">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
                                </span>
                              )}
                            </span>
                            <Library size={14} />
                          </button>

                          {/* Recent Maps Toggle Item & Submenu */}
                          <div className="relative border-t border-white/10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsRecentMapsOpen(!isRecentMapsOpen);
                              }}
                              className="w-full px-4 py-2.5 text-right text-mystic-200 hover:bg-white/10 hover:text-gold-400 transition-colors flex items-center justify-between gap-2 text-xs font-bold"
                            >
                              <div className="flex items-center gap-1.5">
                                <ChevronRight
                                  size={13}
                                  className={`text-mystic-400 transition-transform duration-200 hidden md:inline ${isRecentMapsOpen ? 'translate-x-0.5 text-gold-400' : ''}`}
                                />
                                <ChevronDown
                                  size={13}
                                  className={`text-mystic-400 transition-transform duration-200 md:hidden ${isRecentMapsOpen ? 'rotate-180' : ''}`}
                                />
                                {recentMaps.length > 0 && (
                                  <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-gold-500/20 text-gold-400 font-mono font-bold">
                                    {recentMaps.length}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2.5">
                                <span className="whitespace-nowrap">מפות אחרונות</span>
                                <History size={14} className="text-gold-400/80" />
                              </div>
                            </button>

                            {/* Submenu: Desktop = TO THE RIGHT | Mobile = OVERLAY SLIGHTLY TO THE RIGHT */}
                            {isRecentMapsOpen && (
                              <div
                                className="
                                  /* Mobile (< md): Absolute overlay below the button with exact parent width */
                                  absolute top-full right-0 w-full z-[60] mt-0.5 translate-x-2
                                  /* Desktop (md+): Flyout submenu to the RIGHT with exact parent width */
                                  md:translate-x-0 md:top-0 md:left-full md:ml-2 md:w-52 md:right-auto
                                  bg-[#0a0714] backdrop-blur-2xl border border-gold-500/40 rounded-xl shadow-2xl shadow-black/90
                                  overflow-hidden animate-in fade-in zoom-in-95 duration-200
                                "
                                dir="rtl"
                              >
                                <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs font-bold text-gold-300">
                                  <span className="text-[10px] text-mystic-400 font-mono">({recentMaps.length}/10)</span>
                                  <div className="flex items-center gap-1.5">
                                    <span>היסטוריית מפות</span>
                                    <History size={13} className="text-gold-400" />
                                  </div>
                                </div>
                                <div className="max-h-[185px] overflow-y-auto scrollbar-thin scrollbar-thumb-gold-500/20 py-1">
                                  {recentMaps.length === 0 ? (
                                    <div className="px-4 py-3 text-center text-[11px] text-mystic-400">
                                      אין מפות בהיסטוריה
                                    </div>
                                  ) : (
                                    recentMaps.map((mapItem) => {
                                      const itemKey = getMapDeduplicationKey(mapItem);
                                      const fullName = `${mapItem.firstName} ${mapItem.familyName || ''}`.trim();
                                      return (
                                        <div
                                          key={itemKey}
                                          onClick={() => handleRecentMapClick(mapItem)}
                                          className="px-3 py-1.5 hover:bg-gold-500/10 cursor-pointer flex items-center justify-between group/recent transition-colors text-right"
                                        >
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeMap(itemKey);
                                            }}
                                            className="p-1 text-mystic-400 hover:text-red-400 rounded transition-colors opacity-70 group-hover/recent:opacity-100"
                                            title="הסר מההיסטוריה"
                                          >
                                            <X size={12} />
                                          </button>
                                          <div className="flex flex-col items-end overflow-hidden max-w-[150px]">
                                            <span className="text-xs text-gold-100 font-semibold truncate group-hover/recent:text-gold-300">
                                              {fullName}
                                            </span>
                                            <span className="text-[10px] text-mystic-400 font-mono">
                                              {mapItem.birthDate}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {isAdmin && (
                            <>
                              <button
                                onClick={() => {
                                  closeDropdown();
                                  navigate("/admin");
                                }}
                                className="w-full px-4 py-2.5 text-right text-purple-400 hover:bg-purple-500/10 transition-colors flex items-center justify-end gap-3 text-xs font-bold border-t border-white/10"
                              >
                                <span>לוח בקרה מנהל</span>
                                <Shield size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  closeDropdown();
                                  navigate("/admin?tab=prompt-engine");
                                }}
                                className="w-full px-4 py-2.5 text-right text-gold-400 hover:bg-gold-500/10 transition-colors flex items-center justify-end gap-3 text-xs font-bold border-t border-white/10"
                              >
                                <span>חדר מנוע פרומפט</span>
                                <Cpu size={14} className="text-gold-400" />
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => {
                              closeDropdown();
                              const currentParams = new URLSearchParams(window.location.search);
                              const activeMapId = currentParams.get("id") || myMap?.id;
                              navigate(activeMapId ? `/chat?id=${activeMapId}` : "/chat");
                            }}
                            className="w-full px-4 py-2.5 text-right text-gold-400 hover:bg-white/10 hover:text-gold-300 transition-colors flex items-center justify-end gap-3 border-t border-white/10 text-xs font-bold"
                          >
                            <span className="whitespace-nowrap">ייעוץ AI</span>
                            <Sparkles size={14} className="text-gold-400" />
                          </button>

                          <button
                            onClick={() => {
                              closeDropdown();
                              setIsCheckoutOpen(true);
                            }}
                            className="w-full px-4 py-2 text-right bg-gradient-to-r from-gold-500/10 to-purple-500/10 hover:from-gold-500/20 hover:to-purple-500/20 text-gold-300 transition-colors flex items-center justify-end gap-1.5 border-y border-gold-500/20 text-xs font-black"
                          >
                            <span className="whitespace-nowrap">{credits || 0} קרדיטים</span>
                            <span className="text-sm shrink-0">⚡</span>
                          </button>

                          <div className="h-px bg-white/10 mx-2 my-1" />

                          <button
                            onClick={() => {
                              closeDropdown();
                              navigate("/account-settings");
                            }}
                            className="w-full px-4 py-2.5 text-right text-mystic-200 hover:bg-white/10 hover:text-gold-400 transition-colors flex items-center justify-end gap-3 text-xs font-bold"
                          >
                            <span>ניהול חשבון</span>
                            <Settings size={14} />
                          </button>

                          <button
                            onClick={async () => {
                              try {
                                await signOut();
                                toast.success('התנתקת בהצלחה');
                              } catch (err) {
                                console.error('Sign out failed:', err);
                                toast.error('שגיאה בהתנתקות, נסה שוב');
                              } finally {
                                closeDropdown();
                              }
                            }}
                            className="w-full px-4 py-2.5 text-right text-red-400 hover:bg-red-500/10 transition-colors flex items-center justify-end gap-3 text-xs font-bold"
                          >
                            <span>התנתק</span>
                            <LogOut size={14} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <ConnectButton
                  onClick={() => setIsLoginModalOpen(true)}
                  icon={LogIn}
                  className="px-3 py-1.5 md:px-4 md:py-2 text-sm shrink-0"
                >
                  <span className="inline font-black">כניסה</span>
                </ConnectButton>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay - Updated offset to match dynamic height */}
        {isMobileMenuOpen && (
          <div data-no-swipe="true" className="sm:hidden fixed inset-x-0 top-[55px] bottom-0 z-[100] bg-mystic-1000/95 backdrop-blur-2xl animate-in fade-in slide-in-from-top-4 duration-300 h-[calc(100vh-56px)]">
            <nav className="flex flex-col p-6 gap-1 h-full overflow-y-auto w-full">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between py-3 px-4 rounded-xl border border-white/5 bg-white/[0.01] hover:bg-gradient-to-r hover:from-purple-500/10 hover:to-gold-500/5 hover:border-gold-500/20 text-mystic-100 hover:text-gold-200 transition-all duration-300 group shadow-sm hover:shadow-[0_0_15px_rgba(168,85,247,0.05)]"
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={18} className="text-mystic-300 group-hover:text-gold-400 group-hover:scale-110 transition-all duration-300" />
                    <span className="font-semibold text-sm transition-colors duration-300">{item.label}</span>
                  </div>
                  <ChevronLeft size={16} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 text-gold-400 transition-all duration-300" />
                </button>
              ))}

              {/* Extra Mobile-only links or info could go here */}
              <div className="mt-auto py-8 text-center shrink-0">
                <p className="text-xs text-mystic-500 opacity-60">מעבדה לחקר הרוח © 2026 </p>
              </div>
            </nav>
          </div>
        )}
      </header>

      <Modal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        title="כניסה למערכת"
      >
        <SocialLogin onEmailLoginClick={() => {
          setIsLoginModalOpen(false);
          setIsEmailLoginModalOpen(true);
        }} />
      </Modal>

      <EmailLoginModal
        isOpen={isEmailLoginModalOpen}
        onClose={() => setIsEmailLoginModalOpen(false)}
      />

      <ClaimSelfMapModal
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
      />

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onSuccess={() => setIsCheckoutOpen(false)}
      />
    </>
  );
};
